import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'

import React from 'react'
import { createRoot } from 'react-dom/client'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { PageHistoryManager } from 'wdc-cube'
import { MainPresenter } from './main'
import { registerViews as registerWaitingViews } from './components/waiting/views'
import { registerViews as registerMultiselectViews } from './components/multiselect/views'
import { registerViews as registerSearchBoxViews } from './components/searchbox/views'
import { registerViews as registerMainViews, MainView } from './main/views'
import { registerViews as registerOverviewViews } from './modules/overview/views'
import { registerViews as registerTheConnectionViews } from './modules/the-connection/views'
import { registerViews as registerTheActingViews } from './modules/the-acting/views'
import { registerViews as registerTheMapViews } from './modules/the-map/views'
import { registerViews as registerTheMapMarkDetailViews } from './modules/the-map/mark_details/views'
import { registerViews as registerTheConsumerMarket } from './modules/the-consumer-market/views'
import { registerViews as registerMercadoVet } from './modules/mercado-vet/views'
import { registerViews as registerAnaliseDesempenho } from './modules/analise-desempenho/views'
import { registerViews as registerRDStationViews } from './modules/rd-station/views'
import { registerViews as registerUsersManagerViews } from './modules/admin/user-manager/views'
import { registerViews as registerUsersManagerFormViews } from './modules/admin/user-manager/form/views'
import { registerViews as registerChangeUserPasswordFormViews } from './modules/admin/user-manager/form/change-password/views'
import { registerViews as registerGroupsManagerViews } from './modules/admin/group-manager/views'
import { registerViews as registerGroupsManagerFormViews } from './modules/admin/group-manager/form/views'
import { registerViews as registerConfigurationsViews } from './modules/admin/configurations/views'
import { registerViews as registerAccessAuditViews } from './modules/admin/access-audit/views'
import { registerViews as registerBillingViews } from './modules/admin/billing/views'
import { registerViews as registerRestrictedViews } from './modules/restricted/views'
import { registerViews as registerUserProfileViews } from './modules/user-profile/views'
import { registerViews as registerGroupProfileViews } from './modules/group-profile/views'
import { registerViews as registerMcAnaliseViews } from './modules/motor-credito/analise/views'
import { registerViews as registerMcAnaliseFormViews } from './modules/motor-credito/analise/form/views'
import { registerViews as registerMcRegraFormViews } from './modules/motor-credito/analise/regra/views'
import { registerViews as registerMcCondicaoFormViews } from './modules/motor-credito/analise/regra/condicao/views'
import { registerViews as registerMcAnaliseSelectViews } from './modules/motor-credito/analise/select/views'
import { registerViews as registerMcBorderoViews } from './modules/motor-credito/bordero/views'
import { registerViews as registerMcBorderoFormViews } from './modules/motor-credito/bordero/form/views'
import { registerViews as registerMcResultadoAnaliseViews } from './modules/motor-credito/bordero/resultado/views'
import { registerViews as registerMcResultadoDetalhadoViews } from './modules/motor-credito/bordero/resultado-detalhado/views'
import { registerViews as registerMcDialogoResultadoViews } from './modules/motor-credito/bordero/resultado-detalhado/dialogo-resultado/views'
import { registerViews as registerMcStatusProcessamentoViews } from './modules/motor-credito/bordero/status-processamento/views'
import { registerViews as registerMcGrupoViews } from './modules/motor-credito/grupo/views'
import { registerViews as registerMcGrupoFormViews } from './modules/motor-credito/grupo/form/views'
import { registerViews as registerMcMonitoramentoViews } from './modules/motor-credito/integracao/views'
import { registerViews as registerMcMonitoramentoFormViews } from './modules/motor-credito/integracao/form/views'
import { registerViews as registerMcVisaoCedenteViews } from './modules/motor-credito/integracao/resultado/views'
import { registerViews as registerMcPfViews } from './modules/motor-credito/pf/views'
import { registerViews as registerMcPjViews } from './modules/motor-credito/pj/views'
import { registerViews as registerMcSerasaViews } from './modules/motor-credito/serasa/views'
import { registerViews as registerMcSerasaHistoryViews } from './modules/motor-credito/serasa-history/views'
import { registerViews as registerMcSerproViews } from './modules/motor-credito/serpro/views'
import { buildCube } from './Cube'

import { MuiTheme } from './theme'
import { LocaleProvider } from 'src/utils/views'

registerWaitingViews()
registerMultiselectViews()
registerSearchBoxViews()
registerMainViews()
registerOverviewViews()
registerTheConnectionViews()
registerTheActingViews()
registerTheMapViews()
registerTheMapMarkDetailViews()
registerTheConsumerMarket()
registerMercadoVet()
registerAnaliseDesempenho()
registerRDStationViews()
registerUsersManagerViews()
registerUsersManagerFormViews()
registerChangeUserPasswordFormViews()
registerGroupsManagerViews()
registerGroupsManagerFormViews()
registerConfigurationsViews()
registerAccessAuditViews()
registerBillingViews()
registerRestrictedViews()
registerUserProfileViews()
registerGroupProfileViews()

// :: Motor de Crédito
registerMcPjViews()
registerMcPfViews()
registerMcSerasaViews()
registerMcSerasaHistoryViews()
registerMcSerproViews()
registerMcGrupoViews()
registerMcGrupoFormViews()
registerMcAnaliseViews()
registerMcAnaliseFormViews()
registerMcRegraFormViews()
registerMcCondicaoFormViews()
registerMcAnaliseSelectViews()
registerMcBorderoViews()
registerMcBorderoFormViews()
registerMcResultadoAnaliseViews()
registerMcResultadoDetalhadoViews()
registerMcDialogoResultadoViews()
registerMcStatusProcessamentoViews()
registerMcMonitoramentoViews()
registerMcMonitoramentoFormViews()
registerMcVisaoCedenteViews()

buildCube()

const historyManager = new PageHistoryManager(true)

function App() {
    const presenter = React.useMemo(() => new MainPresenter(historyManager), [])

    React.useEffect(() => {
        const handleOnComponentWillUnmount = presenter.initialize()
        return () => {
            handleOnComponentWillUnmount()
        }
    }, [presenter])

    const locale = LocaleProvider.get()
    //adapterLocale={locale.fns}

    return (
        <MuiTheme>
            <LocalizationProvider
                dateAdapter={AdapterDayjs}
                adapterLocale="pt-br"
                localeText={locale.mui.components.MuiLocalizationProvider.defaultProps.localeText}
            >
                <MainView scope={presenter.scope} />
            </LocalizationProvider>
        </MuiTheme>
    )
}

const container = document.getElementById('root')
if (container) {
    const root = createRoot(container)
    root.render(<App />)
}
